const cities = [
  { name: "Winter Garden", zips: "34787" },
  { name: "Ocoee", zips: "34761" },
  { name: "Clermont", zips: "34711, 34714, 34715" },
  { name: "Windermere", zips: "34786" },
  { name: "Dr. Phillips", zips: "32819" },
  { name: "Apopka", zips: "32703, 32712" },
  { name: "Gotha", zips: "34734" },
  { name: "Oakland", zips: "34760" },
  { name: "Minneola", zips: "34715" },
  { name: "Montverde", zips: "34756" },
  { name: "Groveland", zips: "34736" },
  { name: "Four Corners", zips: "34747" },
];

const benefits = [
  {
    icon: "ri-computer-line",
    title: "Start 100% Online",
    desc: "No office visit required to begin. Complete your order from home or downtown Winter Garden in minutes.",
  },
  {
    icon: "ri-truck-line",
    title: "Ship Your Documents",
    desc: "Use overnight shipping to send your documents directly to us — we handle the rest.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Expert Document Review",
    desc: "Every application is reviewed by our team before submission to reduce errors and delays.",
  },
  {
    icon: "ri-calendar-check-line",
    title: "Reserved Agency Access",
    desc: "We hold reserved submission slots at the regional passport agency — no public appointment needed.",
  },
];

export default function WinterGardenLocalAreaSection() {
  return (
    <section id="service-area" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Service Area</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Serving Winter Garden & Western Orange County
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Whether you are in Winter Garden's historic downtown, a newer subdivision off Tilden Road, or anywhere in western Orange County and south Lake County, Fast Passport Center makes expedited passport processing simple — no drive to a passport agency, no lines, no guessing.
          </p>
        </div>

        {/* Full-width editorial intro */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 md:p-8 mb-12">
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            Winter Garden occupies a unique position in the Orlando metro area — a town that has managed to preserve its historic small-town character while growing into one of the most desirable family suburbs in Central Florida. Located about 14 miles west of downtown Orlando along the western shore of Lake Apopka, Winter Garden is the kind of place where Friday nights mean the farmers market on Plant Street, Saturday mornings mean a bike ride on the West Orange Trail, and Sunday afternoons mean strolling past restored 1920s storefronts that now house craft breweries, artisan bakeries, and independent boutiques. The population has surged past 46,000 as families discover what locals have always known: Winter Garden offers Orlando proximity without Orlando intensity.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            What makes Winter Garden's travel demographics distinct is the blend of old-Florida families and newer transplants. The historic downtown core — anchored by the Garden Theatre, the Plant Street Market, and the Winter Garden Heritage Foundation — draws visitors from across Central Florida, but the residents who call Winter Garden home are increasingly young families who moved here for the excellent schools (West Orange High School consistently ranks among Orange County's best), the safe neighborhoods, and the quality of life that downtown Orlando suburbs simply cannot replicate. These families travel internationally with the same frequency as any affluent Orlando suburb — Caribbean cruises, European vacations, Mexican beach getaways — but they also have ties to the agricultural and equestrian communities that stretch west toward Clermont and south toward Lake County.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            Winter Garden's proximity to both Disney and Universal (roughly 20-25 minutes east) creates a unique vacation pattern. Many Winter Garden families are Annual Pass holders who treat the parks as their weekend backyard — which means their "big trip" budget often goes toward international travel rather than domestic vacations. Spring break in the Bahamas, summer in Europe, Christmas in Mexico — these are the travel rhythms of Winter Garden families who see Orlando's attractions as local entertainment and save their passports for destinations that require them. The town's rapid growth has also attracted medical professionals from the nearby Orlando Health and AdventHealth systems, engineers and tech workers commuting to the Lake Nona Medical City and downtown Orlando, and remote workers who chose Winter Garden specifically for its balance of accessibility and authenticity.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            The geographic reality of Winter Garden matters for passport processing. The closest regional passport agency is in Miami, approximately 235 miles south on the Florida Turnpike — a drive of nearly four hours that few Winter Garden residents would consider for a same-day passport appointment. Orlando itself has no regional passport agency, which means Winter Garden travelers facing an expired passport before a Caribbean cruise or European vacation have no local government option for emergency processing. This is precisely why our courier-based expedited service has become the go-to solution for western Orange County residents — you start online from your Winter Garden home, ship your documents overnight, and your passport arrives within days without a single drive to a government office.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed">
            Fast Passport Center has processed expedited passports for Winter Garden travelers across every demographic — families heading to Disney Cruise Line departures from Port Canaveral, medical professionals attending conferences in Europe, college students from nearby Valencia College and the University of Central Florida leaving for study abroad programs, and retirees from the newer 55+ communities west of town discovering that their passport expired sometime between their last cruise and their next one. Our service is designed for exactly this reality: you live in a beautiful suburb, you have travel plans, and you need your passport handled efficiently without disrupting your Winter Garden routine.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left — FPC Office + How It Works */}
          <div>
            {/* FPC Office Card */}
            <div className="bg-[#1A365D] rounded-lg p-7 mb-8 text-white">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#F6C344] flex-shrink-0">
                  <i className="ri-building-2-line text-[#1A365D] text-xl"></i>
                </div>
                <div>
                  <p className="font-bold text-base">Fast Passport Center — Delray Beach</p>
                  <p className="text-white/60 text-xs mt-0.5">401 W Atlantic Ave #351, Delray Beach, FL 33444</p>
                </div>
              </div>
              <p className="text-white/70 text-sm mb-1">
                <i className="ri-map-pin-line text-[#F6C344] mr-1.5"></i>
                Nearest office to Winter Garden — approximately 200 miles southeast via Florida Turnpike
              </p>
              <p className="text-white/50 text-xs mb-5 ml-5">Courier pickup available throughout Orange County</p>
              <div className="border-t border-white/10 pt-5">
                <p className="text-white/80 text-sm leading-relaxed mb-4">
                  After signing up online, choose what works best for you: <strong className="text-white">courier pickup</strong> from your Winter Garden home in most cases, <strong className="text-white">ship with our prepaid label</strong> from any UPS/FedEx location near downtown, or <strong className="text-white">schedule a drop-off</strong> at our Delray Beach office. Most clients ship overnight — no visit required.
                </p>
                <a
                  href="https://fastpassportcenter.com/checkout/"
                  className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-5 py-2.5 rounded-md transition-all whitespace-nowrap"
                >
                  Start My Passport <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>

            {/* How FPC Helps */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {benefits.map((b, i) => (
                <div key={i} className="bg-white rounded-lg p-5 border border-gray-200">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#F6C344]/15 mb-3">
                    <i className={`${b.icon} text-[#1A365D] text-base`}></i>
                  </div>
                  <p className="font-bold text-[#1A365D] text-sm mb-1">{b.title}</p>
                  <p className="text-gray-500 text-xs leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Cities We Serve + Trust strip */}
          <div>
            <div className="bg-white rounded-lg p-7 mb-6 border border-gray-200">
              <h3 className="font-bold text-[#1A365D] text-sm uppercase tracking-wide mb-5">
                <i className="ri-map-pin-2-line text-[#F6C344] mr-2"></i>
                Cities & Communities We Serve
              </h3>
              <div className="space-y-1.5">
                {cities.map((city, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between py-2.5 px-3 bg-[#F7F8FA] rounded-md"
                  >
                    <div className="flex items-center gap-2.5">
                      <i className="ri-checkbox-circle-fill text-[#F6C344] text-sm"></i>
                      <span className="text-[#1A365D] font-medium text-sm">{city.name}</span>
                    </div>
                    {city.zips && (
                      <span className="text-gray-400 text-xs font-mono">{city.zips}</span>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-gray-500 text-xs mt-4 leading-relaxed">
                Anywhere in western Orange County and south Lake County — start online, ship overnight, and we handle the rest. No need to drive to a passport agency.
              </p>
            </div>

            {/* Internal links */}
            <div className="bg-white rounded-lg p-5 mb-6 border border-gray-200">
              <p className="text-gray-400 text-xs font-semibold uppercase tracking-widest mb-3">Nearby Cities We Serve</p>
              <div className="flex flex-wrap gap-2">
                {[
                  { name: "Orlando", href: "/orlando" },
                  { name: "Winter Park", href: "/winter-park" },
                  { name: "Windermere", href: "/windermere" },
                  { name: "Lake Mary", href: "/lake-mary" },
                  { name: "Celebration", href: "/celebration" },
                  { name: "Tampa", href: "/tampa" },
                ].map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-[#F7F8FA] border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5 whitespace-nowrap"
                  >
                    {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
                  </a>
                ))}
              </div>
            </div>

            {/* Trust credentials strip */}
            <div className="bg-[#1A365D] rounded-lg p-5">
              <p className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-4">Why Travelers Choose Fast Passport Center</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: "ri-government-line", value: "Registered Courier", label: "U.S. Department of State" },
                  { icon: "ri-award-line", value: "A+ Rating", label: "Better Business Bureau" },
                  { icon: "ri-calendar-check-line", value: "20+ Years", label: "Serving Travelers Nationwide" },
                  { icon: "ri-customer-service-2-line", value: "Real Humans", label: "Live Support — Not a Bot" },
                ].map((item, i) => (
                  <a
                    key={i}
                    href="https://fastpassportcenter.com"
                    className="flex items-center gap-2.5 bg-white/5 hover:bg-white/10 rounded-md px-3 py-2.5 transition-colors cursor-pointer"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
                      <i className={`${item.icon} text-sm text-[#F6C344]`}></i>
                    </div>
                    <div className="text-left">
                      <span className="text-white text-xs font-bold block leading-tight">{item.value}</span>
                      <span className="text-white/50 text-xs leading-tight block">{item.label}</span>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}